import React, { useState, useRef, useEffect } from "react";
import { Button, Card, Input, Avatar, Typography, notification, Spin, Divider, message } from "antd";
import {
    MessageOutlined,
    SendOutlined,
    CloseOutlined,
    UserOutlined,
    RobotOutlined,
    CustomerServiceOutlined,
    ArrowLeftOutlined
} from "@ant-design/icons";
import "../css/ChatBot.css";
import PublicServ from "../service/PublicServ";
import ChatServ from "../service/ChatServ";
import AuthServ from "../service/AuthServ";
import io from "socket.io-client";
import dayjs from "dayjs";

const { Text } = Typography;

const PREDEFINED_QUESTIONS = [
    "Where is my order?",
    "How to process a return?",
    "Payment Issues",
    "Contact Us",
    "Talk to Support"
];

const ChatBot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [chatMode, setChatMode] = useState("selection"); // 'selection', 'ai', 'human'
    const [messages, setMessages] = useState([
        { text: "Hi! How can I help you today?", isUser: false }
    ]);
    const [inputText, setInputText] = useState("");
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const [user, setUser] = useState(AuthServ.getUser());
    const [activeConversation, setActiveConversation] = useState(null);
    const socket = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(scrollToBottom, [messages, isOpen, chatMode]);

    // Socket setup for human chat
    useEffect(() => {
        if (isOpen && chatMode === "human" && activeConversation) {
            setupSocket();
        }
        return () => {
            if (socket.current) {
                socket.current.disconnect();
            }
        };
    }, [isOpen, chatMode, activeConversation]);

    const setupSocket = () => {
        const socketUrl = 'http://localhost:4000';
        socket.current = io(socketUrl);

        socket.current.on('connect', () => {
            socket.current.emit('joinConversation', activeConversation._id);
        });

        socket.current.on('receiveMessage', (newMessage) => {
            setMessages(prev => [...prev, { text: newMessage.text, isUser: newMessage.sender === user?._id || newMessage.sender === user?.id, createdAt: newMessage.createdAt }]);
        });
    };

    const handleOpenChat = () => {
        setIsOpen(true);
        setChatMode("selection");
    };

    const handleCloseChat = () => {
        setIsOpen(false);
        setChatMode("selection");
        setMessages([{ text: "Hi! How can I help you today?", isUser: false }]);
        setInputText("");
        setActiveConversation(null);
        notification.success({
            message: "Thank you!",
            description: "Thank you for using the chat. We're here to help anytime!",
            placement: "topRight",
            duration: 3
        });
    };

    const switchToAI = () => {
        setChatMode("ai");
        setMessages([{ text: "I am your AI Assistant. How can I help you today?", isUser: false }]);
    };

    const switchToHuman = async () => {
        if (!AuthServ.isLogged()) {
            message.info("Please login to chat with our support team.");
            return;
        }

        setLoading(true);
        try {
            const adminRes = await ChatServ.getSupportAdmin();
            const adminId = adminRes.data._id;

            const convRes = await ChatServ.startConversation({ receiverId: adminId });
            setActiveConversation(convRes.data);

            // Load previous messages
            const msgRes = await ChatServ.getMessages(convRes.data._id);
            const history = msgRes.data.map(m => ({
                text: m.text,
                isUser: m.sender === (user?._id || user?.id),
                createdAt: m.createdAt
            }));

            setMessages(history.length > 0 ? history : [{ text: `You are now connected with ${adminRes.data.firstName}. How can we help?`, isUser: false }]);
            setChatMode("human");
        } catch (error) {
            console.error("Error connecting to human support:", error);
            notification.error({
                message: "Support Unavailable",
                description: "All our agents are busy. Please try again later or use AI chat."
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSendMessage = async (text) => {
        if (!text.trim()) return;

        const currentUserId = user?._id || user?.id;

        if (chatMode === "ai") {
            const newMessages = [...messages, { text, isUser: true }];
            setMessages(newMessages);
            setInputText("");

            setTimeout(async () => {
                let botResponse = "Thank you for your message.";
                let shouldNotifySupport = false;

                if (text.toLowerCase().includes("order")) {
                    botResponse = "You can track your orders in the 'My Orders' section of your dashboard.";
                } else if (text.toLowerCase().includes("return")) {
                    botResponse = "To return an item, go to 'My Orders', select the order, and click 'Return Item'.";
                } else if (text.toLowerCase().includes("payment")) {
                    botResponse = "For payment issues, please check if your bank account was debited or try a different payment method.";
                } else if (text.toLowerCase().includes("contact")) {
                    botResponse = "You can reach us at support@nearzon.com. Would you like to talk to our human support team?";
                } else if (text.toLowerCase().includes("support")) {
                    botResponse = "I can switch you to a human agent. Click 'Talk to Human' at any time.";
                } else {
                    botResponse = "I'm still learning! For complex queries, you can talk to our human support team.";
                }

                setMessages((prev) => [...prev, { text: botResponse, isUser: false }]);
            }, 800);
        } else if (chatMode === "human") {
            const other = activeConversation.participants.find(p => (p._id || p) !== currentUserId);

            const messageData = {
                conversationId: activeConversation._id,
                sender: currentUserId,
                receiver: other._id || other,
                text: text
            };

            socket.current.emit('sendMessage', messageData);
            setInputText("");
        }
    };

    return (
        <div className="chatbot-container">
            {isOpen && (
                <Card
                    className="chatbot-window"
                    title={
                        <div className="pcu-flex-center pcu-gap-8">
                            {chatMode === "human" ? <CustomerServiceOutlined /> : <RobotOutlined />}
                            {chatMode === "selection" ? "Nearzon Support" : chatMode === "ai" ? "AI Assistant" : "Human Support"}
                        </div>
                    }
                    extra={
                        <div className="pcu-flex pcu-gap-5">
                            {chatMode !== "selection" && <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => setChatMode("selection")} />}
                            <Button type="text" icon={<CloseOutlined />} onClick={handleCloseChat} />
                        </div>
                    }
                    bordered={false}
                >
                    {chatMode === "selection" ? (
                        <div className="chatbot-selection">
                            <Text strong className="pcu-block pcu-margin-bottom-20 pcu-text-center">
                                How would you like to proceed?
                            </Text>
                            <Button
                                type="primary"
                                icon={<RobotOutlined />}
                                block
                                className="pcu-h-60 pcu-margin-bottom-15 pcu-radius-12"
                                onClick={switchToAI}
                            >
                                Chat with AI (Instant)
                            </Button>
                            <Button
                                icon={<CustomerServiceOutlined />}
                                block
                                className="pcu-h-60 pcu-radius-12"
                                onClick={switchToHuman}
                                loading={loading}
                            >
                                Chat with Human (Support Team)
                            </Button>
                            <Divider>OR</Divider>
                            <Text type="secondary" className="pcu-font-12 pcu-text-center pcu-block">
                                Our support team is available from 9 AM to 9 PM.
                            </Text>
                        </div>
                    ) : (
                        <>
                            <div className="chatbot-messages">
                                {messages.map((msg, index) => (
                                    <div key={index} className={`message ${msg.isUser ? "user" : "bot"}`}>
                                        <Avatar
                                            size="small"
                                            src={msg.isUser ? user?.profilePhoto : null}
                                            icon={msg.isUser ? (!user?.profilePhoto && <UserOutlined />) : (chatMode === "ai" ? <RobotOutlined /> : <CustomerServiceOutlined />)}
                                            className="message-avatar"
                                        />
                                        <div className="message-bubble">
                                            <Text>{msg.text}</Text>
                                            {msg.createdAt && (
                                                <div className="pcu-font-8 pcu-opacity-50 pcu-margin-top-4">
                                                    {dayjs(msg.createdAt).format('h:mm A')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <div ref={messagesEndRef} />
                            </div>

                            {chatMode === "ai" && (
                                <div className="chatbot-options">
                                    {PREDEFINED_QUESTIONS.map((q, i) => (
                                        <Button
                                            key={i}
                                            size="small"
                                            className="option-btn"
                                            onClick={() => handleSendMessage(q)}
                                        >
                                            {q}
                                        </Button>
                                    ))}
                                </div>
                            )}

                            <div className="chatbot-input">
                                <Input
                                    placeholder="Type a message..."
                                    value={inputText}
                                    onChange={(e) => setInputText(e.target.value)}
                                    onPressEnter={() => handleSendMessage(inputText)}
                                    suffix={
                                        <Button
                                            type="text"
                                            icon={<SendOutlined />}
                                            onClick={() => handleSendMessage(inputText)}
                                        />
                                    }
                                />
                            </div>
                        </>
                    )}
                </Card>
            )}

            {!isOpen && (
                <Button
                    shape="circle"
                    size="large"
                    icon={<MessageOutlined className="pcu-font-24" />}
                    className="chatbot-trigger"
                    onClick={handleOpenChat}
                />
            )}
        </div>
    );
};

export default ChatBot;
